/* eslint-disable import/prefer-default-export */
import config from "@Config/index";
import LangChinaFlag from "@Icons/LangChinaFlag";
import LangEnglishUKFlag from "@Icons/LangEnglishUKFlag";
import LangFranceFlag from "@Icons/LangFranceFlag";
import LangGermanyFlag from "@Icons/LangGermanyFlag";
import LangSpainFlag from "@Icons/LangSpainFlag";
import LangThailandFlag from "@Icons/LangThailandFlag";
import LangTurkeyFlag from "@Icons/LangTurkeyFlag";
import LangVietnamFlag from "@Icons/LangVietnamFlag";

export interface LanguageConfig {
  name: string;
  code: string;
  nativeLabel: string;
  domains: string[];
  flag: any;
  shortDisplayText: string;
  hasRegionalBlog?: boolean;
  /**
   * Exclude this locale from the whitelabel partner builds — a separate product, where a locale
   * nobody scoped must not appear in the picker or generate routes. It still exists on the China
   * build, which lists it and can serve `.com` hosts (`hk.amberstudent.com`).
   */
  notOnWhitelabel?: boolean;
  /**
   * Routes for this locale exist on the China (`.cn`) build only. The `.com` picker still lists
   * it and links out to `.cn` (`PICKER_LANGUAGES`).
   */
  chinaOnly?: boolean;
}

const ALL_LANGUAGES: LanguageConfig[] = [
  {
    name: "english",
    nativeLabel: "English",
    code: "en",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com", "localhost"],
    flag: LangEnglishUKFlag,
    shortDisplayText: "En",
  },
  {
    name: "chinese-simplified",
    nativeLabel: "简体中文",
    code: "zh",
    domains: config.isProduction
      ? ["amberstudent.cn"]
      : ["staging-ali.amberstudent.cn", "china.staging-amber.com"],
    flag: LangChinaFlag,
    shortDisplayText: "中文",
  },
  {
    name: "french",
    nativeLabel: "French",
    code: "fr",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com"],
    flag: LangFranceFlag,
    shortDisplayText: "Fr",
    hasRegionalBlog: true,
  },
  {
    name: "spanish",
    nativeLabel: "Spanish",
    code: "es",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com"],
    flag: LangSpainFlag,

    shortDisplayText: "Es",
    hasRegionalBlog: true,
  },
  {
    name: "german",
    nativeLabel: "German",
    code: "de",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com", "localhost"],
    flag: LangGermanyFlag,
    shortDisplayText: "De",
    hasRegionalBlog: true,
  },
  {
    name: "chinese-traditional-tw",
    nativeLabel: "繁體中文 (中國台灣)",
    code: "zhtw",
    domains: config.isProduction
      ? ["amberstudent.cn"]
      : ["staging-ali.amberstudent.cn", "china.staging-amber.com"],
    flag: LangChinaFlag,
    shortDisplayText: "繁中",
    chinaOnly: true,
  },
  {
    name: "chinese-traditional-hk",
    nativeLabel: "繁體中文 (中國香港)",
    code: "zhhk",
    domains: config.isProduction
      ? ["amberstudent.cn"]
      : ["staging-ali.amberstudent.cn", "china.staging-amber.com"],
    flag: LangChinaFlag,
    shortDisplayText: "繁中HK",
    chinaOnly: true,
  },
  // ---------------------------------------------------------------------------
  // NOINDEX LOCALES — vi / th / tr
  //
  // Launched DELIBERATELY INVISIBLE to search engines, and NOT on the same footing as
  // fr/es/de. For these three there must be:
  //   - no sitemap             (s.base allowlists `%w[fr es de]` and 404s the rest —
  //                             do NOT add these codes there)
  //   - no robots.txt entry    (s.web robots.ejs declares /de|/fr|/es sitemaps —
  //                             do NOT add /vi|/th|/tr lines)
  //   - no hreflang            (HREFLANG_EXCLUDED_LANGS in utils/generateHreflangUrls.ts)
  //   - no crawlable SSR link  (SSR_HIDDEN_LANGS in utils/languageCore.ts)
  //
  // Do NOT "complete the rollout" by copying fr/es/de. Making these indexable is a separate
  // decision requiring SEO sign-off.
  // ---------------------------------------------------------------------------
  {
    name: "vietnamese",
    nativeLabel: "Tiếng Việt",
    code: "vi",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com", "localhost"],
    flag: LangVietnamFlag,
    shortDisplayText: "Vi",
    notOnWhitelabel: true,
  },
  {
    name: "thai",
    nativeLabel: "ไทย",
    code: "th",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com", "localhost"],
    flag: LangThailandFlag,
    shortDisplayText: "Th",
    notOnWhitelabel: true,
  },
  {
    name: "turkish",
    nativeLabel: "Türkçe",
    code: "tr",
    domains: config.isProduction
      ? ["amberstudent.com", "hk.amberstudent.com"]
      : ["staging-amber.com", "hk.staging-amber.com", "localhost"],
    flag: LangTurkeyFlag,
    shortDisplayText: "Tr",
    notOnWhitelabel: true,
  },
];

/**
 * The locales THIS build serves.
 *
 * Filtered at source rather than in each consumer, so route generation
 * (`LANGUAGE_PREFIX_PATTERN`), `getLanguageFromHostname` and the hreflang/canonical helpers can
 * never disagree about which locales exist. On whitelabel a `notOnWhitelabel` locale is absent
 * entirely — its routes are never generated, so `/vi/...` 404s there, which is intended. A
 * `chinaOnly` locale is served by the China build only.
 */
export const languagesForAmberUserWebsite: LanguageConfig[] = ALL_LANGUAGES.filter(
  (lang) =>
    !(lang.notOnWhitelabel && config.IS_WHITELABEL_APP) &&
    !(lang.chinaOnly && !config.IS_CHINA_APP),
);

export const SUPPORTED_LANG_VALUES = languagesForAmberUserWebsite.map((lang) => lang.code);

/** Segments that may appear as the first path component in a language-prefixed route. */
export const LANGUAGE_URL_PREFIXES = SUPPORTED_LANG_VALUES.filter(
  (code) => code !== "en" && code !== "zh",
);

/** The Chinese locales — served from `.cn`; every other locale is served from `.com` (P1D-5521). */
export const CHINA_HEADER_LANGUAGE_CODES = ["zh", "zhtw", "zhhk"] as const;

const PICKER_HEAD = config.IS_CHINA_APP
  ? ["zh", "zhtw", "zhhk", "en"]
  : ["en", "zh", "zhtw", "zhhk"];
const pickerRank = (code: string) => {
  const i = PICKER_HEAD.indexOf(code);
  return i === -1 ? PICKER_HEAD.length : i;
};

/**
 * Every locale, served by this build or not, in browser picker order: `.cn` and `.com` list the
 * same languages (P1D-5521 follow-up). The rest keep `ALL_LANGUAGES` order (sort is stable).
 * Also the lookup table for a locale's home domain, so `.com` can link out to `/zhtw/` on `.cn`.
 */
export const PICKER_LANGUAGES: LanguageConfig[] = [...ALL_LANGUAGES].sort(
  (a, b) => pickerRank(a.code) - pickerRank(b.code),
);

export const REGIONAL_BLOG_LANG_VALUES = languagesForAmberUserWebsite
  .filter((lang) => lang.hasRegionalBlog)
  .map((lang) => lang.code);
